import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchTopTokens, fetchAuctionInfo, tokenImage } from "../lib/api";
import { localized } from "../lib/i18nHelpers";
import BidModal from "../components/BidModal";
import { Gavel, Clock, Lock } from "lucide-react";

const RARITY_COLOR = {
    ultra_legendary: { text: "text-pink-400", border: "border-pink-400/40", glow: "shadow-[0_0_30px_rgba(244,114,182,0.25)]" },
    legendary: { text: "text-amber-300", border: "border-amber-300/50", glow: "shadow-[0_0_28px_rgba(252,211,77,0.28)]" },
    mythic: { text: "text-emerald-400", border: "border-emerald-400/40", glow: "shadow-[0_0_30px_rgba(52,211,153,0.25)]" },
    epic: { text: "text-frost", border: "border-frost/40", glow: "shadow-[0_0_30px_rgba(56,189,248,0.25)]" },
};

export default function AuctionsPage() {
    const { t, i18n } = useTranslation();
    const [tops, setTops] = useState([]);
    const [auctions, setAuctions] = useState({});
    const [bidOn, setBidOn] = useState(null);
    const [filter, setFilter] = useState("live");

    const load = async () => {
        const list = await fetchTopTokens();
        setTops(list);
        const m = {};
        for (const tk of list) m[tk.number] = await fetchAuctionInfo(tk.number);
        setAuctions(m);
    };
    useEffect(() => { load(); }, []);

    const visible = tops.filter((tk) => {
        if (filter === "live") return tk.is_open;
        if (filter === "upcoming") return !tk.is_open && tk.release_phase !== "manual";
        if (filter === "manual") return tk.release_phase === "manual";
        return true;
    });

    const liveCount = tops.filter((tk) => tk.is_open).length;

    return (
        <div data-testid="auctions-page" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2">
                <Gavel className="w-3 h-3" /> {t("auctions.kicker")}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
                {t("auctions.title_1")} <span className="italic text-gold-bright">{t("auctions.title_2")}</span>
            </h1>
            <p className="text-sm font-mono text-slate-400 mt-4 max-w-2xl">{t("auctions.intro")}</p>

            <div className="mt-8 flex flex-wrap gap-2">
                {[
                    { k: "live", l: t("auctions.filter_live", { n: liveCount }) },
                    { k: "upcoming", l: t("auctions.filter_upcoming") },
                    { k: "manual", l: t("auctions.filter_manual") },
                    { k: "all", l: t("auctions.filter_all") },
                ].map((f) => (
                    <button
                        key={f.k}
                        data-testid={`auctions-filter-${f.k}`}
                        onClick={() => setFilter(f.k)}
                        className={`btn-sharp px-4 py-2 text-xs border ${
                            filter === f.k
                                ? "bg-white text-obsidian border-white"
                                : "border-white/15 text-slate-400 hover:text-white hover:border-white/40"
                        }`}
                    >
                        {f.l}
                    </button>
                ))}
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                {visible.map((tk) => {
                    const a = auctions[tk.number];
                    const c = RARITY_COLOR[tk.rarity] || RARITY_COLOR.epic;
                    const img = a?.custom_image_url || tokenImage(tk.lat, tk.lon, 6);
                    const L = localized(tk, i18n);
                    return (
                        <div
                            key={tk.number}
                            data-testid={`auction-card-${tk.number}`}
                            className={`bg-slate-900/40 border border-white/10 hover:${c.border} ${c.glow} transition-all duration-300 flex flex-col md:flex-row group`}
                        >
                            <div className="md:w-2/5 aspect-square md:aspect-auto relative bg-slate-950 border-b md:border-b-0 md:border-r border-white/10 overflow-hidden">
                                {img ? (
                                    <img src={img} alt={L.title}
                                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="font-serif text-3xl text-gold opacity-90 px-4 text-center">
                                            {L.title}
                                        </div>
                                    </div>
                                )}
                                <span className={`absolute top-3 left-3 ${c.text} ${c.border} text-[10px] font-bold px-2 py-1 uppercase tracking-widest border bg-obsidian/70`}>
                                    {t(`auctions.rarity.${tk.rarity}`)}
                                </span>
                                {!tk.is_open && (
                                    <span className="absolute top-3 right-3 bg-slate-800/90 text-slate-400 text-[10px] font-bold px-2 py-1 uppercase tracking-widest border border-white/10 inline-flex items-center gap-1">
                                        <Lock className="w-3 h-3" />
                                        {tk.release_phase === "manual" ? t("auctions.manual") : t("auctions.soon")}
                                    </span>
                                )}
                            </div>
                            <div className="flex-1 p-6 flex flex-col justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                                        {t("detail.parcel")} #{tk.number}
                                    </div>
                                    <div className={`font-serif text-3xl mt-1 ${c.text}`}>{L.title}</div>
                                    {L.quote && (
                                        <div className="font-serif text-base italic text-slate-300 mt-2">
                                            "{L.quote}"
                                        </div>
                                    )}
                                    {L.description && (
                                        <p className="font-mono text-xs text-slate-400 mt-3 leading-relaxed line-clamp-4">
                                            {L.description}
                                        </p>
                                    )}
                                    <div className="font-mono text-[11px] text-slate-500 mt-3">
                                        {tk.lat?.toFixed(4)}°{tk.lat < 0 ? "S" : "N"}{" "}
                                        {Math.abs(tk.lon).toFixed(4)}°{tk.lon < 0 ? "W" : "E"}
                                    </div>
                                    <div className="mt-5 grid grid-cols-2 gap-4">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                                {a?.bid_count ? t("auctions.current_bid") : t("auctions.base_price")}
                                            </div>
                                            <div className={`font-serif text-2xl ${c.text}`}>
                                                {(a?.current_bid ?? tk.price).toLocaleString()}{" "}
                                                <span className="text-xs font-mono">USDC</span>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                                {t("auctions.bids")}
                                            </div>
                                            <div className="font-serif text-2xl">{a?.bid_count ?? 0}</div>
                                        </div>
                                    </div>
                                    {a?.auction_end && tk.is_open && (
                                        <div className="mt-4 text-xs font-mono text-slate-400 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />{" "}
                                            {t("auctions.ends", { date: new Date(a.auction_end).toLocaleString() })}
                                        </div>
                                    )}
                                </div>
                                <button
                                    data-testid={`auction-bid-${tk.number}`}
                                    onClick={() => tk.is_open && setBidOn({ token: tk, auction: a })}
                                    disabled={!tk.is_open}
                                    className={`mt-6 btn-sharp px-4 py-3 text-xs disabled:opacity-50 disabled:cursor-not-allowed ${
                                        tk.is_open ? "bg-gold text-obsidian hover:opacity-90" : "border border-white/15 text-slate-400"
                                    }`}
                                >
                                    {tk.is_open
                                        ? t("auctions.place_bid")
                                        : tk.release_phase === "manual"
                                          ? t("auctions.released_manually")
                                          : tk.release_phase === "public_sale"
                                            ? t("auctions.opens_public")
                                            : t("auctions.coming_soon")}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <BidModal
                open={!!bidOn}
                onClose={() => setBidOn(null)}
                token={bidOn?.token}
                auction={bidOn?.auction}
                onSuccess={load}
            />
        </div>
    );
}
